import { Component, OnInit, HostListener, ViewChild } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormControl,
} from "@angular/forms";
import { Router } from "@angular/router";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import {
  SignaturePredefined,
  SaveSignatureRequest,
} from "../signatures.interface";
import { SignaturesService } from "../signatures.service";
import { Observable, of, merge, BehaviorSubject, combineLatest } from "rxjs";
import { catchError, debounceTime, switchMap, map } from "rxjs/operators";
import { VariablePaletteComponent } from "../variable-palette/variable-palette.component";
import { MatDialog } from "@angular/material/dialog";
import { NameInputDialogComponent } from "../../shared/name-input-dialog/name-input-dialog.component";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";
import { EditorComponent } from "../../shared/editor/editor.component";
import { DigitalCardService } from "../../digital-cards/digital-card.service";
import type { DigitalCardProfileItem } from "../../digital-cards/digital-cards.interface";
import type { EmployeePickerItem } from "../../employees/employees.interface";
import { AuthService } from "../../auth/auth.service";

@Component({
  selector: "app-signature-create",
  templateUrl: "./signature-create.component.html",
  styleUrls: ["./signature-create.component.scss"],
  standalone: false,
})
export class SignatureCreateComponent implements OnInit {
  @ViewChild(VariablePaletteComponent)
  variablePalette!: VariablePaletteComponent;
  @ViewChild("signatureEditor") signatureEditor!: EditorComponent;

  form = new UntypedFormGroup({
    name: new UntypedFormControl(""),
    content: new UntypedFormControl(""),
    addonBannerId: new UntypedFormControl(null),
    addonAdditionalBannerId: new UntypedFormControl(null),
    addonLinkId: new UntypedFormControl(null),
    addonDisclaimerId: new UntypedFormControl(null),
    userAvatarWidth: new UntypedFormControl(null, Validators.min(0)),
    userAvatarHeight: new UntypedFormControl(null, Validators.min(0)),
    userAvatarBorderRadius: new UntypedFormControl(null, [
      Validators.min(0),
      Validators.max(50),
    ]),
    qrDigitalCardWidth: new UntypedFormControl(null, Validators.min(0)),
    digitalCardProfileId: new UntypedFormControl(null),
    phoneLabel: new UntypedFormControl(null),
    mobileLabel: new UntypedFormControl(null),
    emailLabel: new UntypedFormControl(null),
    customLabel: new UntypedFormControl(null),
    customLabelVariable: new UntypedFormControl(null),
    labelColor: new UntypedFormControl(null),
    labelTextColor: new UntypedFormControl(null),
  });

  setting = false;
  loading = true;
  preview$!: Observable<SafeHtml>;
  previewEmployee: EmployeePickerItem | null = null;
  private readonly previewEmployee$ =
    new BehaviorSubject<EmployeePickerItem | null>(null);
  activeSection = "addons";
  private pageDataReady = false;
  private addonsReady = false;
  customLabelVariableOptions: string[] = [];
  digitalCardTemplates: DigitalCardProfileItem[] = [];
  digitalCardTemplatesLoading = false;
  digitalCardTemplatesLoaded = false;
  readonly identityLabel = (value: string) => value;
  readonly digitalCardTemplateLabel = (template: DigitalCardProfileItem) =>
    template.displayName || "";
  readonly digitalCardTemplateValue = (template: DigitalCardProfileItem) =>
    template.profileId;

  public predefined$!: Observable<SignaturePredefined[]>;

  constructor(
    private service: SignaturesService,
    private toast: ToastrService,
    private router: Router,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private digitalCardService: DigitalCardService,
    private auth: AuthService,
  ) {}

  get digitalCardEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.digitalCard;
  }

  @HostListener("document:keydown.enter", ["$event"])
  onKeydownHandler(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const isInEditor =
      target.closest(".editor-wrapper") ||
      target.closest(".tox-tinymce") ||
      target.closest(".code-textarea");

    if (!isInEditor && !this.setting) {
      event.preventDefault();
      this.save();
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.predefined$ = this.service.predefined();
    this.service
      .getCustomLabelVariableOptions()
      .pipe(catchError(() => of([] as string[])))
      .subscribe((opts) => {
        this.customLabelVariableOptions = opts;
        this.pageDataReady = true;
        this.finishInitialLoad();
      });

    const initial$ = of(this.form.value);
    const changes$ = this.form.valueChanges.pipe(debounceTime(400));
    this.preview$ = combineLatest([
      merge(initial$, changes$),
      this.previewEmployee$,
    ]).pipe(
      switchMap(
        ([values, employee]: [
          Record<string, unknown>,
          EmployeePickerItem | null,
        ]) =>
          this.service.preview({
            ...values,
            ...(employee?.email?.trim()
              ? { previewEmployeeEmail: employee.email.trim() }
              : {}),
          }),
      ),
      map((html: string) =>
        this.sanitizer.bypassSecurityTrustHtml(
          DOMPurify.sanitize(html ?? "", {
            ALLOWED_TAGS: [
              "a",
              "b",
              "br",
              "em",
              "i",
              "img",
              "p",
              "span",
              "strong",
              "table",
              "td",
              "tr",
              "div",
              "ul",
              "ol",
              "li",
            ],
          }),
        ),
      ),
    );
  }

  onAddonsReady(): void {
    this.addonsReady = true;
    this.finishInitialLoad();
  }

  private finishInitialLoad(): void {
    if (this.pageDataReady && this.addonsReady) {
      this.loading = false;
    }
  }

  onPreviewEmployeeChange(employee: EmployeePickerItem | null): void {
    this.previewEmployee = employee;
    this.previewEmployee$.next(employee);
  }

  toggleSection(section: "addons" | "dynamicVariables" | "variables"): void {
    this.activeSection = section;
  }

  save(installSignature = false) {
    if (!this.form.get("name")?.value) {
      this.showNameInputDialog(installSignature);
      return;
    }

    this.performSave(installSignature);
  }

  private performSave(installSignature = false) {
    this.setting = true;
    const raw = this.form.getRawValue() as SaveSignatureRequest;
    const formData: SaveSignatureRequest = {
      ...raw,
      addonLinkId: raw.addonLinkId || null,
      addonBannerId: raw.addonBannerId || null,
      addonAdditionalBannerId: raw.addonAdditionalBannerId || null,
      addonDisclaimerId: raw.addonDisclaimerId || null,
      digitalCardProfileId: raw.digitalCardProfileId || null,
      phoneLabel: raw.phoneLabel || null,
      mobileLabel: raw.mobileLabel || null,
      emailLabel: raw.emailLabel || null,
      customLabel: raw.customLabel || null,
      customLabelVariable: raw.customLabelVariable || null,
      labelColor: raw.labelColor || null,
      labelTextColor: raw.labelTextColor || null,
    };

    void this.service
      .save(formData, installSignature)
      .toPromise()
      .then((response: { templateId: string }) => {
        this.toast.success(this.translate.instant("footers.added") as string);
        if (installSignature) {
          void this.router.navigate(["signatures", response.templateId]);
        } else {
          this.goToSignatures();
        }
      })
      .catch((error: unknown) => {
        const errorObj = error as {
          error?: { message?: string } | string;
          message?: string;
        };
        let errorMessage = "";
        if (typeof errorObj?.error === "object" && errorObj?.error?.message) {
          errorMessage = errorObj.error.message;
        } else if (errorObj?.message) {
          errorMessage = errorObj.message;
        } else if (typeof errorObj?.error === "string") {
          errorMessage = errorObj.error;
        }
        if (
          errorMessage.includes("TEMPLATE_LIMIT_REACHED") ||
          errorMessage.includes("Template limit reached")
        ) {
          this.dialog.open(LimitPlanDialogComponent, {
            width: "500px",
            maxWidth: "90vw",
            data: { plan: "basic", type: "template" },
          });
        } else {
          this.toast.error(
            this.translate.instant("footers.addError") as string,
          );
        }
      })
      .finally(() => (this.setting = false));
  }

  private showNameInputDialog(installSignature = false) {
    const dialogRef = this.dialog.open(NameInputDialogComponent, {
      width: "500px",
      data: {
        title: this.translate.instant("footers.enterName") as string,
        placeholder: this.translate.instant("footers.name") as string,
        currentName: (this.form.get("name")?.value as string) || "",
        actionType: installSignature
          ? ("saveAndInstall" as const)
          : ("save" as const),
        saveButtonText: this.translate.instant("save") as string,
        saveAndInstallButtonText: this.translate.instant(
          "saveAndInstall",
        ) as string,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { name?: string; action?: string } | undefined) => {
        if (result) {
          this.form.get("name")?.setValue(result.name || "");
          if (result.action === "save") {
            this.performSave(false);
          } else if (result.action === "saveAndInstall") {
            this.performSave(true);
          }
        }
      });
  }

  saveAndInstall() {
    if (!this.form.get("name")?.value) {
      this.showNameInputDialog(true);
      return;
    }

    this.performSave(true);
  }

  load = (tpl: { content: string }) =>
    this.form.patchValue({ content: tpl.content });

  goToSignatures = () => {
    void this.router.navigate(["signatures"]);
  };

  getActiveEditor(): EditorComponent | null {
    if (this.signatureEditor) {
      return this.signatureEditor;
    }
    return null;
  }

  loadDigitalCardTemplates(): void {
    if (this.digitalCardTemplatesLoaded || this.digitalCardTemplatesLoading) {
      return;
    }
    this.digitalCardTemplatesLoading = true;
    this.digitalCardService.getProfiles({ templatesOnly: true }).subscribe({
      next: (res) => {
        this.digitalCardTemplates = res.profiles ?? [];
        this.digitalCardTemplatesLoaded = true;
        this.digitalCardTemplatesLoading = false;
      },
      error: () => {
        this.digitalCardTemplatesLoading = false;
      },
    });
  }

  getDigitalCardTemplateDisplayName(profileId: string | null): string {
    if (!profileId) return "";
    const t = this.digitalCardTemplates.find((p) => p.profileId === profileId);
    return t?.displayName ?? profileId;
  }

  get contentControl(): FormControl<string> {
    return this.form.get("content") as FormControl<string>;
  }

  private normalizeHex(v: string): string {
    const s = v.replace(/^#/, "").trim();
    if (/^[0-9a-fA-F]{6}$/.test(s)) return "#" + s;
    if (/^[0-9a-fA-F]{3}$/.test(s))
      return "#" + s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
    return v.startsWith("#") ? v : "#" + v;
  }

  private isValidHex(v: string): boolean {
    const s = v.startsWith("#") ? v.slice(1) : v;
    return /^[0-9a-fA-F]{3}$/.test(s) || /^[0-9a-fA-F]{6}$/.test(s);
  }

  onLabelColorChange(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.form.patchValue({ labelColor: this.normalizeHex(v) });
  }

  onLabelColorHexBlur(event: Event): void {
    const v = (event.target as HTMLInputElement).value?.trim() || "";
    if (this.isValidHex(v)) {
      this.form.patchValue({ labelColor: this.normalizeHex(v) });
    }
  }

  onLabelTextColorChange(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.form.patchValue({ labelTextColor: this.normalizeHex(v) });
  }

  onLabelTextColorHexBlur(event: Event): void {
    const v = (event.target as HTMLInputElement).value?.trim() || "";
    if (this.isValidHex(v)) {
      this.form.patchValue({ labelTextColor: this.normalizeHex(v) });
    }
  }
}
